import React, { useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { waLink } from './constants';
import { trackWhatsAppClick } from '../../utils/analytics';
import { RaioXFields } from './RaioXFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', whatsapp: '', company: '', instagram: '', segment: '', goal: '' };
const STEP_FIELDS = [['name', 'whatsapp'], ['company', 'instagram', 'segment'], ['goal']];

const buildMessage = (f) =>
  `Olá, Anderson! Quero meu Raio-X da Marca.\n\nNome: ${f.name}\nEmpresa: ${f.company}\nInstagram: ${f.instagram}\nSegmento: ${f.segment}\nObjetivo: ${f.goal}`;

export const RaioXForm = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const stepValid = STEP_FIELDS[step].every((k) => form[k].trim().length >= 2) && (step !== 0 || form.whatsapp.replace(/\D/g, '').length >= 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!stepValid) return;
    if (step < 2) return setStep(step + 1);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/raio-x`, form);
      trackWhatsAppClick('raio-x-form', data.segment);
      toast.success('Pedido recebido! Abrindo o WhatsApp...');
      window.location.href = waLink(buildMessage(data));
    } catch {
      toast.error('Não foi possível enviar. Confira os dados e tente de novo.');
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="relative rounded-[28px] border border-white/10 bg-[#11171E]/90 backdrop-blur-xl p-7 sm:p-10 shadow-[0_40px_120px_-40px_rgba(78,205,196,0.35)]" data-testid="raio-x-form">
      <div className="flex items-center justify-between">
        <span className="br-mono text-[10px] text-[var(--br-muted)]" data-testid="raio-x-step-indicator">Etapa {step + 1} de 3</span>
        <div className="flex gap-1.5">{[0, 1, 2].map((i) => <span key={i} className={`h-1 w-8 rounded-full transition-colors ${i <= step ? 'bg-[var(--br-teal)]' : 'bg-white/10'}`} />)}</div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="mt-8 space-y-4 min-h-[260px]">
          <RaioXFields step={step} form={form} set={set} />
        </motion.div>
      </AnimatePresence>
      <div className="mt-8 flex gap-3">
        {step > 0 && (
          <button type="button" onClick={() => setStep(step - 1)} className="rounded-full border border-white/15 px-5 py-4 hover:border-white/40 transition-colors" data-testid="raio-x-back" aria-label="Voltar">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <button type="submit" disabled={!stepValid || sending} data-testid={step < 2 ? 'raio-x-next' : 'raio-x-submit'} className="flex-1 inline-flex items-center justify-center gap-3 rounded-full bg-[var(--br-teal)] text-black font-bold py-4 transition-[opacity,background-color] duration-300 hover:bg-white disabled:opacity-40 disabled:hover:bg-[var(--br-teal)]">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : step < 2 ? 'Continuar' : 'Enviar e abrir WhatsApp'}
          {!sending && <ArrowRight className="w-5 h-5" />}
        </button>
      </div>
      <p className="mt-5 text-center text-xs text-[var(--br-muted)]">Seus dados ficam só comigo. Sem spam, sem compromisso.</p>
    </form>
  );
};
